import {
  Brain,
  Globe,
  BrainCircuit,
  Microscope,
  Users,
  Pill,
  Stethoscope,
  Heart,
  FileText,
  Search,
  TrendingUp,
  BookOpen,
  FlaskConical,
  Activity,
  Dna,
  Atom,
  Target,
  Database,
  Shield,
  Calculator,
  Box,
  Link,
  Hash,
  Scale,
  ClipboardCheck,
  Cpu,
  Fingerprint,
  Library,
  RefreshCw,
  LayoutGrid,
  View,
  Layers,
  Zap,
  Leaf,
  Waves,
  AlertTriangle,
  ShieldAlert,
  Crosshair,
  Network,
  GitMerge,
  Binary,
  FileEdit
} from "lucide-react";
import { AgentMode } from "../services/chat-provider";

// Mode configuration with mode info, description, and tasks with individual titles, text, icons, and colors
export const ModeInfos = {
  [AgentMode.DEFAULT]: {
    icon: Brain,
    title: "Moremi",
    description: "Chat with your AI medical assistant for quick answers and general health guidance",
    tasks: [
      { title: "Symptoms", text: "What are the common symptoms of type 2 diabetes?", icon: Activity, color: "text-blue-500 dark:text-blue-400" },
      { title: "Treatment", text: "Explain the first-line treatment for hypertension", icon: Heart, color: "text-red-500 dark:text-red-400" },
      { title: "Drug Interactions", text: "What medications should be avoided with warfarin?", icon: Pill, color: "text-purple-500 dark:text-purple-400" },
      { title: "Lab Results", text: "What does an elevated HbA1c level indicate?", icon: FileText, color: "text-amber-500 dark:text-amber-400" }
    ]
  },
  [AgentMode.WEB_SEARCH]: {
    icon: Globe,
    title: "Moremi Search",
    description: "Get answers backed by real-time web searches for the latest medical information and research",
    tasks: [
      { title: "Guidelines", text: "What are the latest WHO guidelines for managing diabetes?", icon: Search, color: "text-cyan-500 dark:text-cyan-400" },
      { title: "Research", text: "What do recent studies say about vitamin D deficiency and immunity?", icon: TrendingUp, color: "text-emerald-500 dark:text-emerald-400" },
      { title: "WHO Recommendations", text: "What are the current WHO recommendations for malaria prevention?", icon: Globe, color: "text-indigo-500 dark:text-indigo-400" },
      { title: "Immunotherapy", text: "What are the latest developments in CAR-T cell therapy for cancer?", icon: FlaskConical, color: "text-pink-500 dark:text-pink-400" }
    ]
  },
  [AgentMode.AGENT]: {
    icon: BrainCircuit,
    title: "Moremi Agent",
    description: "Advanced AI that can use multiple tools and reason through complex medical problems step-by-step",
    tasks: [
      { title: "Complex Cases", text: "What are the diagnostic considerations for a patient with chest pain, shortness of breath, and elevated troponin?", icon: BrainCircuit, color: "text-violet-500 dark:text-violet-400" },
      { title: "Treatment Options", text: "Compare treatment options for community-acquired pneumonia in adults", icon: Stethoscope, color: "text-teal-500 dark:text-teal-400" },
      { title: "Differential Diagnosis", text: "What is the differential diagnosis for persistent fever and joint pain?", icon: Brain, color: "text-fuchsia-500 dark:text-fuchsia-400" },
      { title: "Drug Safety", text: "What are the interactions between metformin and contrast dye?", icon: Pill, color: "text-orange-500 dark:text-orange-400" }
    ]
  },
  [AgentMode.DEEP_RESEARCH]: {
    icon: Microscope,
    title: "Moremi Deep Research",
    description: "Comprehensive in-depth research with detailed analysis, evidence synthesis, and thorough exploration",
    tasks: [
      { title: "Literature Review", text: "Conduct a comprehensive literature review on checkpoint inhibitors in melanoma treatment", icon: BookOpen, color: "text-rose-500 dark:text-rose-400" },
      { title: "Evidence Analysis", text: "Analyze the evidence for GLP-1 agonists in cardiovascular risk reduction", icon: TrendingUp, color: "text-sky-500 dark:text-sky-400" },
      { title: "Clinical Trials", text: "Compare outcomes of different anticoagulation strategies in atrial fibrillation", icon: FlaskConical, color: "text-lime-500 dark:text-lime-400" },
      { title: "Emerging Therapies", text: "What are the emerging gene therapies for sickle cell disease?", icon: Microscope, color: "text-yellow-500 dark:text-yellow-400" }
    ]
  },
  [AgentMode.MOREMI_CO_RESEARCHER]: {
    icon: Users,
    title: "Moremi Co-Researcher",
    description: "Specialized research assistant with access to advanced biological and medical research tools",
    tasks: [
      { 
        title: "Comparative Toxicology", 
        text: "Compare liver toxicity profiles of structural analogs gefitinib and erlotinib", 
        icon: Scale, 
        color: "text-orange-600 dark:text-orange-400" 
      },
      { 
        title: "Antibody Annotation", 
        text: "Classify immunoglobulin family and label structural domains for the provided sequence\nEVQLQQSGPELVKPGSSVKISCKGSGYTFTDYNMEWVKQSHGKSLEWIGVINPNNRFTSYNQNFRGKATLTVDKSSSTAYMDLRSLTSEDSAVYFCAGSRWYQYDYWGQGTTLTVSSASTKGPSVFPLAPSSKSTSGGTAALGCLVKDYFPEPVTVSWNSGALTSGVHTFPAVLQSSGLYSLSSVVTVPSSSLGTQTYICNVNHAPSNTKVDKKVEPKSCD", 
        icon: Shield, 
        color: "text-indigo-600 dark:text-indigo-400" 
      },
      { 
        title: "ADMET Profiling", 
        text: "Evaluate PubChem CID 4091 for oral drug developability, solubility, and metabolism", 
        icon: Pill, 
        color: "text-teal-600 dark:text-teal-400" 
      },
      { 
        title: "Compound Validation", 
        text: "Validate Aspirin (CID 2244) SMILES and analyze off-target interactions and toxicity", 
        icon: ClipboardCheck, 
        color: "text-rose-600 dark:text-rose-400" 
      }
    ],
    tools: [
      { name: "GlycoEP", description: "Predict N, O, and C-linked glycosylation sites", icon: Atom },
      { name: "NCBI BLAST", description: "Sequence alignment and homology searching", icon: Search },
      { name: "SWISS-MODEL", description: "Automated protein structure homology modeling", icon: Box },
      { name: "IEDB ElliPro", description: "Structure-based antibody epitope prediction", icon: Target },
      { name: "ABCPred", description: "Artificial neural network based B-cell epitope prediction", icon: Cpu },
      { name: "PRODIGY", description: "Predict binding affinity for protein-protein complexes", icon: Link },
      { name: "IEDB MHC Suite", description: "MHC Class I & II binding and T-cell epitope prediction", icon: Fingerprint },
      { name: "ProtParam", description: "Compute physical and chemical protein parameters", icon: Calculator },
      { name: "IgBLAST", description: "Immunoglobulin variable domain sequence analysis", icon: Dna },
      { name: "ANARCI", description: "Antibody numbering and receptor classification", icon: Hash },
      { name: "Humatch", description: "Antibody humanization and homology analysis", icon: Users },
      { name: "InterPro", description: "Functional analysis of proteins and family classification", icon: Database },
      { name: "ADMET-AI", description: "Machine learning based ADMET property prediction", icon: FlaskConical },
      { name: "RDKit", description: "Open-source toolkit for cheminformatics and SMILES logic", icon: Library },
      { name: "OpenBabel", description: "Chemical file format converter and descriptor tool", icon: RefreshCw },
      { name: "AutoDock Vina", description: "Molecular docking and receptor-ligand interactions", icon: LayoutGrid },
      { name: "py3dmol", description: "3D molecular visualization and rendering", icon: View },
      { name: "RCSB Database", description: "Search and retrieve experimentally determined 3D macromolecular structures", icon: Layers },
      { name: "AlphaFold Database", description: "Access AI-predicted protein structure models for the entire proteome", icon: Network },
      { name: "ANPDB", description: "Browse African natural products and their bioactivities and structural data", icon: Leaf },
      { name: "Aggrescan", description: "Predict protein aggregation hotspots and amyloid-forming regions", icon: GitMerge },
      { name: "Algpred", description: "Predict allergenicity of protein sequences using multiple methods", icon: AlertTriangle },
      { name: "Toxinpred", description: "Predict and design toxic or non-toxic peptides from sequence", icon: ShieldAlert },
      { name: "Bcepred", description: "Predict linear B-cell epitopes using physicochemical properties", icon: Crosshair },
      { name: "ENA Database", description: "Explore European Nucleotide Archive sequences and annotations", icon: Dna },
      { name: "UniProt Database", description: "Access curated protein sequence and functional information", icon: Database },
      { name: "Ensembl Database", description: "Retrieve genome annotations and variant data for eukaryotes", icon: Dna },
      { name: "CELLxGENE Census", description: "Explore single-cell RNA-seq datasets and cell types", icon: Layers },
      { name: "FDA Database", description: "Search FDA-approved drugs, labels and regulatory information", icon: ClipboardCheck },
      { name: "Monarch Database", description: "Connect phenotypes to genes and diseases via genotype", icon: Activity },
      { name: "Medchem Database", description: "Browse medicinal chemistry data and drug-like properties", icon: Pill },
      { name: "Matchms Database", description: "Compare mass spectrometry spectra of metabolites", icon: Zap },
      { name: "JASPAR Database", description: "Access transcription factor binding profile models", icon: Dna },
      { name: "GTEx Database", description: "Explore gene expression across human tissues", icon: TrendingUp },
      { name: "ClinPGx Database", description: "Clinical pharmacogenomics guidelines and gene-drug associations", icon: Fingerprint },
      { name: "Molfeat", description: "Molecular featurization hub for property calculations", icon: Calculator },
      { name: "HMMER (phmmer)", description: "Search protein sequences against profile hidden Markov models", icon: Search },
      { name: "MAFFT", description: "Multiple sequence alignment with various algorithms", icon: Binary },
      { name: "Primer3", description: "Design PCR primers for DNA amplification", icon: Cpu },
      { name: "AUGUSTUS", description: "Predict gene structures and coding regions in genomes", icon: Dna },
      { name: "ViennaRNA (RNAfold)", description: "Predict RNA secondary structure and folding energy", icon: Activity },
      { name: "GC Content Calculator", description: "Calculate GC content percentage in DNA/RNA sequences", icon: Calculator },
      { name: "Sequence Entropy Calculator", description: "Compute Shannon entropy for sequence diversity", icon: Binary },
      { name: "k-mer Counter", description: "Count k-length words in sequence datasets", icon: Hash },
      { name: "k-mer Frequency Analyzer", description: "Analyze k-mer distributions and patterns", icon: TrendingUp },
      { name: "Codon Usage Calculator", description: "Calculate codon usage bias and redundancy", icon: Calculator }
    ]
  },
  [AgentMode.CANVAS_AGENT]: {
    icon: FileEdit,
    title: "Moremi Reviewer",
    description: "Upload your paper, choose a journal, and get a review with tracked edits",
    tasks: [
      { title: "Review Draft", text: "Review this manuscript draft and suggest improvements aligned with Nature Medicine standards", icon: FileText, color: "text-blue-500 dark:text-blue-400" },
      { title: "Check Methods", text: "Critically review the Methods section of this paper for reproducibility and clarity", icon: ClipboardCheck, color: "text-emerald-500 dark:text-emerald-400" },
      { title: "Peer Review", text: "Generate a peer review for this research article following standard academic review guidelines", icon: BookOpen, color: "text-purple-500 dark:text-purple-400" },
      { title: "Improve Writing", text: "Revise this discussion section for conciseness, logical flow, and academic tone", icon: FileEdit, color: "text-amber-500 dark:text-amber-400" }
    ]
  }
};
